import React, {
    useState,
    useEffect,
    useReducer,
    useCallback,
    useContext,
} from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";

import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import FolderOpenIcon from "@material-ui/icons/FolderOpen";

import MainContainer from "../../components/MainContainer";
import { PageHeader, StatusBadge } from "../../components/ui";
import EmptyState from "../../components/EmptyState";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import FileModal from "../../components/FileModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
// import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";
import { getTokens } from "../../styles/tokens";

const reducer = (state, action) => {
    if (action.type === "LOAD_FILES") {
        const files = action.payload;
        const newFiles = [];

        files.forEach((fileList) => {
            const fileListIndex = state.findIndex((s) => s.id === fileList.id);
            if (fileListIndex !== -1) {
                state[fileListIndex] = fileList;
            } else {
                newFiles.push(fileList);
            }
        });

        return [...state, ...newFiles];
    }

    if (action.type === "UPDATE_FILES") {
        const fileList = action.payload;
        const fileListIndex = state.findIndex((s) => s.id === fileList.id);

        if (fileListIndex !== -1) {
            state[fileListIndex] = fileList;
            return [...state];
        } else {
            return [fileList, ...state];
        }
    }

    if (action.type === "DELETE_FILE") {
        const fileListId = action.payload;

        const fileListIndex = state.findIndex((s) => s.id === fileListId);
        if (fileListIndex !== -1) {
            state.splice(fileListIndex, 1);
        }
        return [...state];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const useStyles = makeStyles((theme) => {
    const t = getTokens(theme.palette.type, theme.palette.primary.main);
    return {
    filters: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 12,
        marginBottom: 16,
    },
    search: {
        width: "min(320px, 100%)",
        [theme.breakpoints.down("xs")]: { width: "100%" },
    },
    searchIcon: { color: t.textMuted },
    mainPaper: {
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxShadow: t.shadow.card,
    },
    tableScroll: {
        flex: 1,
        minHeight: 0,
        overflow: "auto",
        ...theme.scrollbarStyles,
    },
    table: { minWidth: 420 },
    name: { fontWeight: 600, color: t.text },
    description: {
        marginTop: 2,
        maxWidth: 640,
        fontSize: 12.5,
        color: t.textSecondary,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    actionsCell: { whiteSpace: "nowrap" },
    deleteButton: { color: t.semantic.error },
}});

const FileLists = () => {
    const classes = useStyles();

    //   const socketManager = useContext(SocketContext);
    const { user, socket } = useContext(AuthContext);


    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [selectedFileList, setSelectedFileList] = useState(null);
    const [deletingFileList, setDeletingFileList] = useState(null);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [searchParam, setSearchParam] = useState("");
    const [files, dispatch] = useReducer(reducer, []);
    const [fileListModalOpen, setFileListModalOpen] = useState(false);

    const fetchFileLists = useCallback(async () => {
        try {
            const { data } = await api.get("/files/", {
                params: { searchParam, pageNumber },
            });
            dispatch({ type: "LOAD_FILES", payload: data.files });
            setHasMore(data.hasMore);
            setLoading(false);
        } catch (err) {
            toastError(err);
        }
    }, [searchParam, pageNumber]); 

    useEffect(() => {
        dispatch({ type: "RESET" });
        setPageNumber(1);
    }, [searchParam]);

    useEffect(() => {
        setLoading(true);
        const delayDebounceFn = setTimeout(() => {
            fetchFileLists();
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchParam, pageNumber, fetchFileLists]);

    useEffect(() => {
        // const socket = socketManager.GetSocket(user.companyId, user.id);

        const onFileEvent = (data) => {
            if (data.action === "update" || data.action === "create") {
                dispatch({ type: "UPDATE_FILES", payload: data.files });
            }

            if (data.action === "delete") {
                dispatch({ type: "DELETE_FILE", payload: +data.fileId });
            }
        };

        socket.on(`company-${user.companyId}-file`, onFileEvent);
        return () => {
            socket.off(`company-${user.companyId}-file`, onFileEvent);
        };
    }, [socket]);

    const handleOpenFileListModal = () => {
        setSelectedFileList(null);
        setFileListModalOpen(true);
    };

    const handleCloseFileListModal = () => {
        setSelectedFileList(null);
        setFileListModalOpen(false);
    };

    const handleSearch = (event) => {
        setSearchParam(event.target.value);
    };

    const handleEditFileList = (fileList) => {
        setSelectedFileList(fileList);
        setFileListModalOpen(true);
    };

    const handleDeleteFileList = async (fileListId) => {
        try {
            await api.delete(`/files/${fileListId}`);
            toast.success(i18n.t("files.toasts.deleted"));
        } catch (err) {
            toastError(err);
        }
        setDeletingFileList(null);
        setSearchParam("");
        setPageNumber(1);

        dispatch({ type: "RESET" });
        setPageNumber(1);
        await fetchFileLists();
    };

    const loadMore = () => {
        setPageNumber((prevState) => prevState + 1);
    };

    const handleScroll = (e) => {
        if (!hasMore || loading) return;
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - (scrollTop + 100) < clientHeight) {
            loadMore();
        }
    };

    return (
        <MainContainer maxWidth>
            <ConfirmationModal
                title={deletingFileList && `${i18n.t("files.confirmationModal.deleteTitle")}`}
                open={confirmModalOpen}
                onClose={setConfirmModalOpen}
                onConfirm={() => handleDeleteFileList(deletingFileList.id)}
            >
                {i18n.t("files.confirmationModal.deleteMessage")}
            </ConfirmationModal>
            <FileModal
                open={fileListModalOpen}
                onClose={handleCloseFileListModal}
                reload={fetchFileLists}
                aria-labelledby="form-dialog-title"
                fileListId={selectedFileList && selectedFileList.id}
            />
            {user.profile === "user" ?
                <ForbiddenPage />
                :
                <>
                    <PageHeader
                        title={i18n.t("files.title")}
                        subtitle="Conjuntos de arquivos prontos para enviar aos clientes."
                        badge={
                            <StatusBadge
                                color="neutral"
                                dot={false}
                                label={String(files.length)}
                            />
                        }
                        actions={
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={handleOpenFileListModal}
                            >
                                {i18n.t("files.buttons.add")}
                            </Button>
                        }
                    />
                    <div className={classes.filters}>
                        <TextField
                            className={classes.search}
                            variant="outlined"
                            size="small"
                            placeholder={i18n.t("contacts.searchPlaceholder")}
                            type="search"
                            value={searchParam}
                            onChange={handleSearch}
                            inputProps={{ "aria-label": i18n.t("contacts.searchPlaceholder") }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" className={classes.searchIcon} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </div>
                    <Paper className={classes.mainPaper} variant="outlined">
                        <div className={classes.tableScroll} onScroll={handleScroll}>
                            <Table size="small" className={classes.table}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{i18n.t("files.table.name")}</TableCell>
                                        <TableCell align="right">
                                            {i18n.t("files.table.actions")}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <>
                                        {files.map((fileList) => (
                                            <TableRow key={fileList.id}>
                                                <TableCell>
                                                    <div className={classes.name}>{fileList.name}</div>
                                                    {fileList.message && (
                                                        <div className={classes.description}>
                                                            {fileList.message}
                                                        </div>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right" className={classes.actionsCell}>
                                                    <Tooltip title="Editar">
                                                        <IconButton
                                                            size="small"
                                                            aria-label="Editar lista de arquivos"
                                                            onClick={() => handleEditFileList(fileList)}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Excluir">
                                                        <IconButton
                                                            size="small"
                                                            aria-label="Excluir lista de arquivos"
                                                            className={classes.deleteButton}
                                                            onClick={(e) => {
                                                                setConfirmModalOpen(true);
                                                                setDeletingFileList(fileList);
                                                            }}
                                                        >
                                                            <DeleteOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {!loading && files.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={2} style={{ borderBottom: "none" }}>
                                                    <EmptyState
                                                        icon={<FolderOpenIcon />}
                                                        title={
                                                            searchParam
                                                                ? "Nenhuma lista encontrada"
                                                                : "Nenhuma lista de arquivos"
                                                        }
                                                        message={
                                                            searchParam
                                                                ? "Tente outro termo de busca."
                                                                : "Crie uma lista com os arquivos que a equipe envia com frequência."
                                                        }
                                                        action={
                                                            !searchParam && (
                                                                <Button
                                                                    variant="contained"
                                                                    color="primary"
                                                                    startIcon={<AddIcon />}
                                                                    onClick={handleOpenFileListModal}
                                                                >
                                                                    {i18n.t("files.buttons.add")}
                                                                </Button>
                                                            )
                                                        }
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {loading && <TableRowSkeleton columns={2} />}
                                    </>
                                </TableBody>
                            </Table>
                        </div>
                    </Paper>
                </>}
        </MainContainer>
    );
};

export default FileLists;
